import type { CSSProperties, ReactNode } from 'react'

import Link from 'next/link'
import { useId, useRef, useState } from 'react'
import {
    ArrowUpRight,
    Check,
    Search,
    Sparkles
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { createRandom } from '@/lib/random'
import { formatCurrency } from '@/lib/format'
import {
    areaPath,
    smoothPath,
    svgId,
    toPoints
} from '@/lib/chart'
import { useInView } from '@/hooks/use-in-view'
import { LogoMark } from '@/components/generals/logo'
import { Reveal } from '@/components/generals/reveal'

type Period = '1M' | '6M' | '1Y' | 'All'

const NET_WORTH = 261230.38

const buildSeries = (seed: number, points: number, change: number): Array<number> => {
    const random = createRandom(seed)
    const start = NET_WORTH - change
    const values: Array<number> = []

    for (let index = 0; index < points; index += 1) {
        const t = index / (points - 1)
        const trend = start + change * (t * t * 0.35 + t * 0.65)
        const noise = (random() - 0.5) * Math.abs(change) * 0.22 * Math.sin(Math.PI * t)

        values.push(trend + noise)
    }

    values[points - 1] = NET_WORTH

    return values
}

const PERIODS: Record<Period, { values: Array<number>, change: number, label: string }> = {
    '1M': { values: buildSeries(3, 30, 3284), change: 3284, label: 'over the past month' },
    '6M': { values: buildSeries(5, 26, 18940), change: 18940, label: 'over 6 months' },
    '1Y': { values: buildSeries(8, 52, 34120), change: 34120, label: 'over the past year' },
    All: { values: buildSeries(13, 48, 161230), change: 161230, label: 'since you joined' }
}

const ACCOUNTS = [
    { name: 'Daily checking', bank: 'Northfield Bank', amount: 8420.16 },
    { name: 'Reserve savings', bank: 'Stillwater', amount: 24900 },
    { name: 'Investment account', bank: 'Lumen Capital', amount: 96310.55 },
    { name: 'Workplace 401(k)', bank: 'Meridian Trust', amount: 132840 },
    { name: 'Rewards card', bank: 'Halcyon', amount: -1240.33 }
]

const SPENDING = [
    { label: 'Home', amount: 1664, color: '#e2f37a' },
    { label: 'Food and dining', amount: 920, color: '#8fae62' },
    { label: 'Shopping and fun', amount: 569, color: '#a3a9f2' },
    { label: 'Utilities', amount: 526, color: '#7fb8b0' },
    { label: 'Getting around', amount: 394, color: '#e8b65d' },
    { label: 'Everything else', amount: 307, color: 'rgba(243,239,230,0.35)' }
]

const BILLS = [
    { name: 'Rent', when: 'Due tomorrow', amount: 2150 },
    { name: 'Electricity', when: 'Due in 4 days', amount: 64.2 },
    { name: 'Broadband', when: 'Due in 7 days', amount: 55 },
    { name: 'Auto insurance', when: 'Due in 12 days', amount: 112.4 }
]

const SIDEBAR = ['Summary', 'Accounts', 'Spending', 'Portfolio', 'Goals', 'Advice']

interface PanelProps {
    title: string
    action?: ReactNode
    index: number
    className?: string
    children: ReactNode
}

const Panel = ({ title, action, index, className, children }: PanelProps) => {
    return (
        <Reveal delay={index * 140} y={36} className={cn('h-full', className)}>
            <section className='hairline h-full rounded-[22px] bg-white/[0.035] p-4 sm:p-5'>
                <header className='flex items-center justify-between gap-3'>
                    <h3 className='text-[13px] font-medium text-cream/80'>
                        {title}
                    </h3>

                    {action}
                </header>

                <div className='mt-4'>
                    {children}
                </div>
            </section>
        </Reveal>
    )
}

const NetWorthPanel = () => {
    const [period, setPeriod] = useState<Period>('1Y')
    const gradientId = `networth-${svgId(useId())}`
    const data = PERIODS[period]
    const points = toPoints(data.values, { width: 600, height: 200, padTop: 12, padBottom: 6 })

    return (
        <Panel
            index={0}
            title='Total net worth'
            className='sm:col-span-2'
            action={(
                <div role='group' aria-label='Time range' className='flex rounded-full bg-white/[0.05] p-0.5'>
                    {(Object.keys(PERIODS) as Array<Period>).map(key => (
                        <button
                            key={key}
                            type='button'
                            aria-pressed={period === key}
                            onClick={() => setPeriod(key)}
                            className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] transition-colors duration-300',
                                period === key ? 'bg-cream text-black' : 'text-cream/55 hover:text-cream'
                            )}
                        >
                            {key}
                        </button>
                    ))}
                </div>
            )}
        >
            <div className='flex flex-wrap items-end justify-between gap-3'>
                <p className='text-3xl font-normal tracking-[-0.04em] tabular-nums text-cream sm:text-4xl'>
                    {formatCurrency(NET_WORTH, { decimals: 2 })}
                </p>

                <p className='flex items-center gap-1 text-xs text-citron'>
                    <ArrowUpRight aria-hidden='true' className='size-3.5' />
                    {formatCurrency(data.change, { sign: true })} {data.label}
                </p>
            </div>

            <div className='relative mt-4 aspect-[600/200] w-full'>
                <svg key={period} viewBox='0 0 600 200' className='absolute inset-0 h-full w-full overflow-visible' aria-hidden='true'>
                    <defs>
                        <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='0%' stopColor='#e2f37a' stopOpacity='0.3' />
                            <stop offset='100%' stopColor='#e2f37a' stopOpacity='0' />
                        </linearGradient>
                    </defs>

                    {[50, 100, 150].map(y => <line key={y} x1='0' x2='600' y1={y} y2={y} stroke='rgba(255,255,255,0.06)' />)}

                    <path d={areaPath(points, 200)} fill={`url(#${gradientId})`} className='motion-fade' style={{ '--fade-delay': '500ms' } as CSSProperties} />
                    <path d={smoothPath(points)} fill='none' stroke='#e2f37a' strokeWidth='2' strokeLinecap='round' pathLength={1} className='motion-draw' />
                </svg>
            </div>
        </Panel>
    )
}

const AccountsPanel = () => {
    return (
        <Panel index={1} title='Accounts' action={<span className='text-[11px] text-cream/40'>5 connected</span>}>
            <ul className='space-y-3'>
                {ACCOUNTS.map(account => (
                    <li key={account.name} className='flex items-center justify-between gap-3'>
                        <span className='min-w-0'>
                            <span className='block truncate text-[13px] text-cream/85'>
                                {account.name}
                            </span>

                            <span className='block text-[11px] text-cream/40'>
                                {account.bank}
                            </span>
                        </span>

                        <span className={cn('text-[13px] tabular-nums', account.amount < 0 ? 'text-cream/55' : 'text-cream')}>
                            {formatCurrency(account.amount, { decimals: 2 })}
                        </span>
                    </li>
                ))}
            </ul>
        </Panel>
    )
}

const SpendingPanel = () => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })
    const total = SPENDING.reduce((sum, item) => sum + item.amount, 0)
    const radius = 40
    const circumference = 2 * Math.PI * radius

    let cursor = 0

    return (
        <Panel index={2} title='Where it went this month'>
            <div ref={ref} className='flex items-center gap-5'>
                <div className='relative size-28 shrink-0'>
                    <svg viewBox='0 0 100 100' className='size-full -rotate-90' aria-hidden='true'>
                        <circle cx='50' cy='50' r={radius} fill='none' stroke='rgba(255,255,255,0.06)' strokeWidth='10' />

                        {SPENDING.map(item => {
                            const length = (item.amount / total) * circumference
                            const start = cursor

                            cursor += length

                            return (
                                <circle
                                    key={item.label}
                                    cx='50'
                                    cy='50'
                                    r={radius}
                                    fill='none'
                                    stroke={item.color}
                                    strokeWidth='10'
                                    strokeDasharray={inView ? `${Math.max(0, length - 1.5)} ${circumference}` : `0 ${circumference}`}
                                    strokeDashoffset={-start}
                                    style={{ transition: 'stroke-dasharray 1.4s cubic-bezier(0.22, 1, 0.36, 1)' }}
                                />
                            )
                        })}
                    </svg>

                    <div className='absolute inset-0 grid place-items-center text-center'>
                        <span>
                            <span className='block text-[15px] font-medium tabular-nums text-cream'>
                                {formatCurrency(total)}
                            </span>

                            <span className='block text-[10px] text-cream/45'>
                                outgoing
                            </span>
                        </span>
                    </div>
                </div>

                <ul className='min-w-0 flex-1 space-y-1.5'>
                    {SPENDING.map(item => (
                        <li key={item.label} className='flex items-center justify-between gap-2 text-[11.5px]'>
                            <span className='flex min-w-0 items-center gap-1.5 text-cream/70'>
                                <span className='size-1.5 shrink-0 rounded-full' style={{ backgroundColor: item.color }} />
                                <span className='truncate'>
                                    {item.label}
                                </span>
                            </span>

                            <span className='tabular-nums text-cream/50'>
                                {Math.round((item.amount / total) * 100)}%
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </Panel>
    )
}

interface GoalsPanelProps {
    depositBoost: number
}

const GoalsPanel = ({ depositBoost }: GoalsPanelProps) => {
    const ref = useRef<HTMLUListElement>(null)
    const inView = useInView(ref, { threshold: 0.4 })
    const goals = [
        { name: 'Safety net', saved: 16800, target: 24000 },
        { name: 'First home fund', saved: 38020 + depositBoost, target: 60000 },
        { name: 'Lisbon in autumn', saved: 2080, target: 4000 }
    ]

    return (
        <Panel index={3} title='Saving toward'>
            <ul ref={ref} className='space-y-4'>
                {goals.map((goal, index) => {
                    const share = Math.round((goal.saved / goal.target) * 100)

                    return (
                        <li key={goal.name}>
                            <div className='flex items-baseline justify-between gap-3 text-[12.5px]'>
                                <span className='text-cream/80'>
                                    {goal.name}
                                </span>

                                <span className='tabular-nums text-cream/50'>
                                    {share}%
                                </span>
                            </div>

                            <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-white/[0.08]'>
                                <div
                                    className='h-full rounded-full bg-[linear-gradient(90deg,#e2f37a,#8fae62)] transition-[width] duration-[1600ms] ease-cinema'
                                    style={{ width: inView ? `${share}%` : '0%', transitionDelay: `${index * 160}ms` }}
                                />
                            </div>

                            <p className='mt-1.5 text-[10.5px] tabular-nums text-cream/40'>
                                {formatCurrency(goal.saved)} of {formatCurrency(goal.target)}
                            </p>
                        </li>
                    )
                })}
            </ul>
        </Panel>
    )
}

interface InsightPanelProps {
    moved: boolean
    onMove: () => void
}

const InsightPanel = ({ moved, onMove }: InsightPanelProps) => {
    const [dismissed, setDismissed] = useState(false)

    return (
        <Panel
            index={5}
            title='Gentle nudge'
            className='sm:col-span-2 xl:col-span-3'
            action={<Sparkles aria-hidden='true' className='size-4 text-citron' />}
        >
            <div aria-live='polite'>
                {moved && (
                    <div className='flex items-start gap-3 rounded-2xl bg-citron/10 p-3.5 text-[13px] leading-relaxed text-cream'>
                        <span className='grid size-6 shrink-0 place-items-center rounded-full bg-citron text-black'>
                            <Check aria-hidden='true' className='size-3.5' />
                        </span>
                        All set. $180 is now in your first home fund, which sits at 64%.
                    </div>
                )}

                {!moved && dismissed && (
                    <p className='rounded-2xl bg-white/[0.04] p-3.5 text-[13px] leading-relaxed text-cream/70'>
                        Of course. I will bring it up again when the month closes.
                    </p>
                )}

                {!moved && !dismissed && (
                    <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                        <p className='max-w-2xl text-[13.5px] leading-relaxed text-cream/85'>
                            Dining came in <span className='text-citron'>$180 under</span> your usual this month. Shall we add it to your first home fund?
                        </p>

                        <div role='group' aria-label='Reply to this suggestion' className='flex shrink-0 flex-wrap gap-2'>
                            <button
                                type='button'
                                onClick={onMove}
                                className='rounded-full bg-cream px-4 py-2 text-xs font-medium text-black transition-colors hover:bg-white'
                            >
                                Add $180
                            </button>

                            <button
                                type='button'
                                onClick={() => setDismissed(true)}
                                className='rounded-full px-4 py-2 text-xs text-cream/60 ring-1 ring-white/15 transition-colors hover:text-cream'
                            >
                                Maybe later
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Panel>
    )
}

const BillsPanel = () => {
    return (
        <Panel index={4} title='Bills ahead' action={<span className='text-[11px] text-cream/40'>Fully funded</span>}>
            <ul className='divide-y divide-white/[0.06]'>
                {BILLS.map(bill => (
                    <li key={bill.name} className='flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0'>
                        <span>
                            <span className='block text-[13px] text-cream/85'>
                                {bill.name}
                            </span>

                            <span className='block text-[11px] text-cream/40'>
                                {bill.when}
                            </span>
                        </span>

                        <span className='text-[13px] tabular-nums text-cream'>
                            {formatCurrency(bill.amount, { decimals: 2 })}
                        </span>
                    </li>
                ))}
            </ul>
        </Panel>
    )
}

/** A wide product mockup whose panels reveal one after another. */
export const DashboardMock = () => {
    const [moved, setMoved] = useState(false)

    return (
        <div className='glass-dark grain relative overflow-hidden rounded-[30px] p-2.5 sm:p-3'>
            <div className='flex items-center gap-3 rounded-[22px] bg-white/[0.03] px-3 py-2.5 sm:px-4'>
                <div className='flex items-center gap-2 pr-2'>
                    <LogoMark className='size-5 text-cream' />

                    <span className='hidden text-sm font-medium tracking-[-0.03em] text-cream sm:inline'>
                        Verdane
                    </span>
                </div>

                <Link
                    href='/ai-intelligence#demo'
                    className='flex flex-1 items-center gap-2 rounded-full bg-white/[0.05] px-3.5 py-2 text-xs text-cream/45 ring-1 ring-white/[0.06] transition-colors hover:text-cream/70 sm:max-w-sm'
                >
                    <Search aria-hidden='true' className='size-3.5' />
                    Ask Verdane a question
                </Link>

                <span className='ml-auto grid size-8 shrink-0 place-items-center rounded-full bg-[linear-gradient(135deg,#e2f37a,#8fae62)] text-[11px] font-semibold text-black' aria-hidden='true'>
                    DR
                </span>
            </div>

            <div className='mt-2.5 grid gap-2.5 lg:grid-cols-[190px_1fr]'>
                <aside aria-label='Dashboard menu' className='hidden rounded-[22px] bg-white/[0.02] p-3 lg:flex lg:flex-col'>
                    <ul className='space-y-0.5 text-[13px]'>
                        {SIDEBAR.map((item, index) => (
                            <li
                                key={item}
                                className={cn(
                                    'flex items-center gap-2 rounded-xl px-3 py-2',
                                    index === 0 ? 'bg-white/[0.07] text-cream' : 'text-cream/45'
                                )}
                            >
                                <span className={cn('size-1.5 rounded-full', index === 0 ? 'bg-citron' : 'bg-white/20')} />
                                {item}
                            </li>
                        ))}
                    </ul>

                    <div className='mt-auto rounded-2xl bg-white/[0.04] p-3'>
                        <p className='text-[11px] text-cream/45'>
                            Assigned advisor
                        </p>

                        <p className='mt-1 font-serif text-lg italic leading-tight text-cream'>
                            Elena Marsh
                        </p>

                        <p className='mt-1 text-[11px] text-cream/50'>
                            Next call Thursday, 11 am ET
                        </p>
                    </div>
                </aside>

                <div className='grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3'>
                    <NetWorthPanel />
                    <AccountsPanel />
                    <SpendingPanel />
                    <GoalsPanel depositBoost={moved ? 180 : 0} />
                    <BillsPanel />
                    <InsightPanel moved={moved} onMove={() => setMoved(true)} />
                </div>
            </div>
        </div>
    )
}
